import { Button, ErrorText, FlexContent, ModalDialog, SideBar, useToggle } from "@rothoblaas/design-system";
import { ComponentType, FC, lazy, ReactNode, Suspense } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { useAppContext } from "../context/appContext";
import { useAuthContext } from "../context/authContext";
import { useIubenda } from "../hooks/useIubenda";
import useI18n from "../hooks/useTranslations";
import AdminMenu from "./AdminMenu";
import { getAdminRoutes } from "./adminRoutes";
import styles from "./AuthenticatedAppShell.module.css";
import NavBar from "./NavBar";
import Error from "./ui/Error";
import Header from "./ui/Header";

const ManageSharingRequest = lazy(() => import("../pages/sharingRequests/ManageSharingRequest"));

type AuthenticatedAppShellProps = {
  Menu: ComponentType;
  children: ReactNode;
};

// Layout of a logged-in, access-granted app: nav bar, admin sidebar and the routes. The session
// bootstrap and the access check happen before this mounts, in AccessGate (see App.tsx).
const AuthenticatedAppShell: FC<AuthenticatedAppShellProps> = ({ Menu, children }) => {
  const { isExpired, logout, permissions, permissionsLoadFailed } = useAuthContext();
  const { Logo } = useAppContext();
  const i18n = useI18n();
  const sidebar = useToggle();
  const isAdminSection = /^\/admin(\/|$)/.test(useLocation().pathname);
  useIubenda();

  return (
    <>
      <NavBar Logo={Logo} Menu={Menu} sidebar={isAdminSection ? sidebar : undefined} />
      <div className={styles.content}>
        {isAdminSection && (
          <SideBar isOpen={sidebar.isOpen} close={sidebar.close}>
            <AdminMenu onClose={sidebar.close} />
          </SideBar>
        )}
        {!isExpired && (
          <div className={styles.pageContent}>
            <Routes>
              {children}

              {/* linked by the sharing request emails, in every app */}
              <Route
                path="project-sharing-requests/:requestId"
                element={
                  <Suspense fallback={null}>
                    <ManageSharingRequest resource="projects" />
                  </Suspense>
                }
              />
              <Route
                path="calculation-sharing-requests/:requestId"
                element={
                  <Suspense fallback={null}>
                    <ManageSharingRequest resource="calculations" />
                  </Suspense>
                }
              />

              {getAdminRoutes(permissions)}

              <Route path="error" element={<Error />} />
              <Route
                path="*"
                element={
                  // Without the permissions (their call failed, e.g. with a 5xx) the admin routes are
                  // not registered, so an existing admin page lands here too: that is an error, not
                  // a page that does not exist.
                  permissionsLoadFailed ? (
                    <>
                      <Header title={i18n.translation.common.error} />
                      <FlexContent padding>
                        <ErrorText text={i18n.translation.common.genericError} />
                      </FlexContent>
                    </>
                  ) : (
                    <>
                      <Header title={i18n.translation.common.notFound} />
                      <FlexContent padding>
                        <ErrorText text={i18n.translation.common.resourceNotFound} />
                      </FlexContent>
                    </>
                  )
                }
              />
            </Routes>
          </div>
        )}
      </div>
      {!!isExpired && (
        <ModalDialog
          onClose={() => logout()}
          title={i18n.translation.common.sessionExpired}
          buttons={<Button onClick={() => logout()}>{i18n.translation.common.close}</Button>}
          isSubmitting={false}
        >
          {i18n.translation.common.sessionExpired}
        </ModalDialog>
      )}
    </>
  );
};

export default AuthenticatedAppShell;
